import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cloud, Package, RefreshCw, ShieldCheck } from 'lucide-react';
import type { Integration, IntegrationProvider } from '@lizrd/contracts';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { ConnectSourceFlow } from '../ConnectSourceFlow';
import { ProviderLogo } from '../ProviderLogo';
import { devDemoConnect, listIntegrations } from '../../lib/api';
import { DEV_TOOLS } from '../../lib/dev';

// Hard per-domain caps (kept in lock-step with the server's IntegrationsService).
const MAX_CLOUD_INTEGRATIONS = 50;
const MAX_CODE_INTEGRATIONS = 100;

const CLOUD_PROVIDERS: { provider: IntegrationProvider; label: string }[] = [
  { provider: 'AWS', label: 'AWS' },
  { provider: 'GCP', label: 'Google Cloud' },
  { provider: 'AZURE', label: 'Azure' },
];
const CODE_PROVIDERS: { provider: IntegrationProvider; label: string }[] = [
  { provider: 'GITHUB', label: 'GitHub' },
  { provider: 'GITLAB', label: 'GitLab' },
];

/** A connected source's status as a design-system Badge with human copy — never the
 * raw enum. PENDING shows a spinner so the wait reads as active work. */
function StatusBadge({ status }: { status: Integration['status'] }) {
  switch (status) {
    case 'CONNECTED':
      return (
        <Badge variant="success" dot>
          Connected
        </Badge>
      );
    case 'PENDING':
    case 'SYNCING':
      return (
        <Badge variant="info" className="gap-1.5">
          <Spinner size="sm" className="h-3 w-3" />
          Verifying…
        </Badge>
      );
    case 'WARNING':
      return (
        <Badge variant="warning" dot>
          Needs attention
        </Badge>
      );
    default:
      return (
        <Badge variant="danger" dot>
          Reconnect needed
        </Badge>
      );
  }
}

const needsReconnect = (s: Integration['status']) =>
  s === 'ERROR' || s === 'WARNING' || s === 'DISCONNECTED';

/**
 * The "connect this domain's sources" body: a required cloud hero card + an optional
 * code card, each with full-width provider pills that open the provider's setup modal
 * directly (skipping the picker), connected-source rows with status + reconnect, and
 * the read-only trust line. Keyed only by envId, so it's shared by the onboarding
 * Connect step and the add-domain modal. Reports whether a CONNECTED cloud exists via
 * `onReadyChange` so the caller can gate its Continue control; the caller supplies the
 * surrounding chrome (title/lead/footer).
 */
export function ConnectPanel({
  envId,
  onReadyChange,
}: {
  envId: string;
  onReadyChange?: (ready: boolean) => void;
}) {
  const qc = useQueryClient();
  // The provider whose setup modal to open — chosen directly from a pill or a
  // reconnect row, so we skip the intermediate "pick a provider" picker.
  const [launch, setLaunch] = useState<IntegrationProvider | null>(null);

  const integrations = useQuery({
    queryKey: ['integrations', envId],
    queryFn: () => listIntegrations(envId),
    refetchInterval: 4000,
  });
  const connected = integrations.data ?? [];
  const clouds = connected.filter((i) => i.kind === 'CLOUD');
  const code = connected.filter((i) => i.kind === 'SCM');
  // Matches the server's prerequisite: a CONNECTED cloud is required; code is optional.
  const cloudConnected = clouds.some((i) => i.status === 'CONNECTED');

  useEffect(() => {
    onReadyChange?.(cloudConnected);
  }, [cloudConnected, onReadyChange]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['integrations', envId] });

  // DEV ONLY (VITE_DEV_TOOLS): one-click fake connected cloud + seeded graph, so
  // the whole flow can be walked locally without real cloud credentials.
  const demoConnect = useMutation({
    mutationFn: () => devDemoConnect(envId),
    onSuccess: () => invalidate(),
  });

  // Plain render helpers (not nested components) so the 4s poll re-render diffs
  // them in place instead of remounting the subtree.
  const sourceRows = (items: Integration[]) => (
    <ul className="mt-1 divide-y divide-hairline">
      {items.map((i) => (
        <li key={i.id} className="flex items-center gap-3 py-2.5">
          <ProviderLogo provider={i.provider} size={16} />
          <span className="min-w-0 flex-1 truncate text-sm text-bone">{i.displayName}</span>
          <StatusBadge status={i.status} />
          {needsReconnect(i.status) && (
            <Button type="button" size="sm" variant="outline" onClick={() => setLaunch(i.provider)}>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              Reconnect
            </Button>
          )}
        </li>
      ))}
    </ul>
  );

  const providerPills = (
    providers: { provider: IntegrationProvider; label: string }[],
    atMax: boolean,
  ) =>
    atMax ? null : (
      <div className="flex flex-wrap gap-2.5">
        {providers.map(({ provider, label }) => (
          <button
            key={provider}
            type="button"
            onClick={() => setLaunch(provider)}
            className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-3 text-sm font-semibold text-bone transition-colors hover:border-iris hover:bg-iris-dim hover:text-iris"
          >
            <ProviderLogo provider={provider} size={18} />
            {label}
          </button>
        ))}
      </div>
    );

  return (
    <>
      <div className="space-y-4">
        {/* Cloud — the required hero. */}
        <div className="rounded-lg border border-iris/30 bg-iris-dim/40 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-iris-dim">
                <Cloud className="h-6 w-6 text-iris" aria-hidden />
              </span>
              <div className="font-display text-base font-bold text-bone">Cloud account</div>
            </div>
            <Badge className="border-transparent bg-iris-dim text-iris">Required</Badge>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted">
            Pick a provider to connect with a guided, read-only role — about a minute each.
          </p>
          <div className="mt-3">
            {providerPills(CLOUD_PROVIDERS, clouds.length >= MAX_CLOUD_INTEGRATIONS)}
          </div>

          {clouds.length > 0 ? (
            sourceRows(clouds)
          ) : (
            <div className="mt-3 flex items-center gap-3 rounded-md border border-dashed border-border px-4 py-3 text-xs text-muted">
              <Cloud className="h-5 w-5 flex-none" aria-hidden />
              No cloud connected yet. Choose a provider above and Lizrd walks you through a
              read-only connection.
            </div>
          )}

          {clouds.length >= MAX_CLOUD_INTEGRATIONS && (
            <p className="mt-3 text-xs text-muted">
              Limit reached — a domain can connect up to {MAX_CLOUD_INTEGRATIONS} cloud accounts.
            </p>
          )}

          <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-muted">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-success" aria-hidden />
            <span>
              <span className="font-semibold text-haze">Read-only.</span> Lizrd never changes or
              deletes your infrastructure — it only reads metadata and billing.
            </span>
          </p>
        </div>

        {/* Code — recessed, optional. */}
        <div className="rounded-lg border border-hairline bg-surface-2 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-surface-3">
                <Package className="h-6 w-6 text-haze" aria-hidden />
              </span>
              <div className="font-display text-base font-bold text-bone">
                Code &amp; repositories
              </div>
            </div>
            <Badge variant="neutral">Optional</Badge>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted">
            Link code to turn a cost finding into the exact pull request that fixes it. Add it now
            or later — we’ll remind you on recommendations that need it.
          </p>
          <div className="mt-3">
            {providerPills(CODE_PROVIDERS, code.length >= MAX_CODE_INTEGRATIONS)}
          </div>
          {code.length > 0 && sourceRows(code)}
        </div>

        {DEV_TOOLS && (
          <div className="rounded-md border border-dashed border-amber/50 bg-amber/5 p-3">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-amber">
              Dev tools (local only)
            </p>
            <p className="mt-1 text-xs text-muted">
              Fake a connected cloud with seeded data so you can clear this step without real
              credentials.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              disabled={demoConnect.isPending}
              onClick={() => demoConnect.mutate()}
            >
              {demoConnect.isPending ? (
                'Connecting…'
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <ProviderLogo provider="SANDBOX" size={14} /> Connect demo cloud
                </span>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Every provider-specific setup modal, shared with the Domain → Integrations
          page so every entry point offers the SAME modals (AWS/GCP/Azure setup,
          GitHub/GitLab). We skip the picker — each pill/reconnect already knows its
          provider, so `directProvider` opens that modal straight away. */}
      <ConnectSourceFlow
        envId={envId}
        active={false}
        directProvider={launch}
        onDirectHandled={() => setLaunch(null)}
        onClosePicker={() => setLaunch(null)}
        onConnected={() => invalidate()}
      />
    </>
  );
}
